import * as React from "react"
import { useForm, usePage } from "@inertiajs/react"
import { Check } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { getAttribution } from "@/lib/analytics"
import { playSound } from "@/lib/sound"
import type { PageProps } from "@/types/inertia"
import { form, interestOptions, spendBands } from "./content"

export interface LeadFormProps {
  /** Page-level attribution stamp saved with the lead (e.g. "econo-pak-case-study"). */
  source?: string
  /** Internal path to return to after submit; the backend appends #apply and
   *  allowlists it. Defaults to the homepage. Passed explicitly (not read from
   *  window) so it is deterministic and SSR-safe. */
  returnTo?: string
}

// The lead application form, as a self-contained card (no section wrapper) so it
// can sit inside the hero or a page's closing CTA. POSTs to /leads via Inertia;
// the field `name`s and the honeypot are the backend contract. On success the
// controller redirects (back to `returnTo`#apply) with a flash notice and we swap
// the form for an inline success state (no reload). Carries the `#apply` anchor so
// header/offer CTAs still scroll to it.
export function LeadForm({ source, returnTo = "/" }: LeadFormProps = {}) {
  const { props } = usePage<PageProps>()
  const errors = props.errors ?? {}

  const leadForm = useForm({
    name: "",
    email: "",
    website: "",
    spend_band: "",
    interest: "",
    message: "",
    // Honeypot — must stay empty for a real human. Bots fill it.
    company_website: "",
    // Which page this form was submitted from: page-level attribution + where the
    // backend redirects back to after submit (allowlisted, #apply appended).
    source: source ?? "",
    return_to: returnTo,
    // Attribution (hidden, not user-editable); populated after mount from the
    // visitor's session so a lead traces back to its source.
    utm_source: "",
    utm_medium: "",
    utm_campaign: "",
    utm_content: "",
    utm_term: "",
  })

  React.useEffect(() => {
    const attribution = getAttribution()
    leadForm.setData((data) => ({
      ...data,
      utm_source: attribution.utm_source ?? "",
      utm_medium: attribution.utm_medium ?? "",
      utm_campaign: attribution.utm_campaign ?? "",
      utm_content: attribution.utm_content ?? "",
      utm_term: attribution.utm_term ?? "",
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function update(field: keyof typeof leadForm.data) {
    return (
      e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
    ) => leadForm.setData(field, e.target.value)
  }

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    leadForm.post("/leads", { preserveScroll: true })
  }

  // A flash notice on this single-flow landing page always means a lead was just
  // accepted, so show the inline success state in place of the form.
  const submitted = Boolean(props.flash?.notice)

  // The one sounded moment on this page's form (docs/specs/site-sound.md §7):
  // the success itself, not the click that started it. Fires on the false->true
  // transition only, so a cold load that already carries the flash stays silent
  // — and would anyway, since no gesture has happened and no AudioContext exists.
  const wasSubmitted = React.useRef(submitted)
  React.useEffect(() => {
    if (submitted && !wasSubmitted.current) playSound("form_success")
    wasSubmitted.current = submitted
  }, [submitted])

  // The form runs as a dark card: the `dark` class re-scopes the palette to the
  // dark set so text reads light, while .lead-form-card (landing.css) gives it a
  // warmer, softer surface than the default dark card and keeps the inputs light.
  if (submitted) {
    return (
      <div
        id="apply"
        data-section="apply"
        className="dark lead-form-card rounded-2xl border p-8 text-center sm:p-10"
      >
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent-faded">
          <Check aria-hidden="true" className="h-6 w-6 text-accent-display" />
        </div>
        <h2 className="section-title mt-5">Application received</h2>
        <p className="section-lead mt-3">{props.flash?.notice}</p>
      </div>
    )
  }

  return (
    <form
      id="apply"
      data-section="apply"
      onSubmit={handleSubmit}
      className="dark lead-form-card rounded-2xl border p-6 sm:p-8"
    >
      <span className="section-eyebrow">{form.eyebrow}</span>
      <p className="meta mt-2">{form.lead}</p>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="lead_name">Name</label>
          <Input
            id="lead_name"
            name="name"
            autoComplete="name"
            required
            aria-invalid={!!errors.name}
            value={leadForm.data.name}
            onChange={update("name")}
            className="mt-1.5"
          />
          {errors.name && <p className="mt-1.5 text-sm text-danger-display">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="lead_email">Email</label>
          <Input
            id="lead_email"
            name="email"
            type="email"
            autoComplete="email"
            required
            aria-invalid={!!errors.email}
            value={leadForm.data.email}
            onChange={update("email")}
            className="mt-1.5"
          />
          {errors.email && <p className="mt-1.5 text-sm text-danger-display">{errors.email}</p>}
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="lead_website">Website or company URL</label>
        <Input
          id="lead_website"
          name="website"
          type="url"
          inputMode="url"
          placeholder="https://"
          required
          aria-invalid={!!errors.website}
          value={leadForm.data.website}
          onChange={update("website")}
          className="mt-1.5"
        />
        {errors.website && <p className="mt-1.5 text-sm text-danger-display">{errors.website}</p>}
      </div>

      <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2">
        <div>
          <label htmlFor="lead_spend_band">Monthly Google Ads spend</label>
          <Select
            id="lead_spend_band"
            name="spend_band"
            required
            aria-invalid={!!errors.spend_band}
            value={leadForm.data.spend_band}
            onChange={update("spend_band")}
            className="mt-1.5"
          >
            <option value="" disabled>
              Select a range
            </option>
            {spendBands.map((band) => (
              <option key={band} value={band}>
                {band}
              </option>
            ))}
          </Select>
          {errors.spend_band && (
            <p className="mt-1.5 text-sm text-danger-display">{errors.spend_band}</p>
          )}
        </div>
        <div>
          <label htmlFor="lead_interest">Interested in</label>
          <Select
            id="lead_interest"
            name="interest"
            required
            aria-invalid={!!errors.interest}
            value={leadForm.data.interest}
            onChange={update("interest")}
            className="mt-1.5"
          >
            <option value="" disabled>
              Select an option
            </option>
            {interestOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
          {errors.interest && (
            <p className="mt-1.5 text-sm text-danger-display">{errors.interest}</p>
          )}
        </div>
      </div>

      <div className="mt-5">
        <label htmlFor="lead_message">Anything else? (optional)</label>
        <textarea
          id="lead_message"
          name="message"
          rows={4}
          aria-invalid={!!errors.message}
          value={leadForm.data.message}
          onChange={update("message")}
          className="form-control form-control-textarea mt-1.5"
        />
        {errors.message && <p className="mt-1.5 text-sm text-danger-display">{errors.message}</p>}
      </div>

      {/* Honeypot — hidden from people, left in the DOM for bots to fill. Not
          display:none so more bots find it; kept out of the tab order and the
          accessibility tree. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-[-9999px] h-0 w-0 overflow-hidden"
      >
        <label htmlFor="company_website">Company website</label>
        <input
          id="company_website"
          name="company_website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={leadForm.data.company_website}
          onChange={update("company_website")}
        />
      </div>

      <div className="mt-6 flex flex-col gap-4">
        <Button type="submit" size="lg" disabled={leadForm.processing}>
          {leadForm.processing ? "Sending..." : form.submitLabel}
        </Button>
        <p className="meta">{form.privacyNote}</p>
      </div>
    </form>
  )
}
